import { useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { useConfirmModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconRedo } from '@components/icons/redo';
import { IconSearch } from '@components/icons/search';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import type { IntegrationWebhookLog, WebhookLogTraveller } from './integrations-types';

const LOGS_PER_PAGE = 10;

export type WebhookLogListProps = {
  logs: IntegrationWebhookLog[];
  onResend?: (log: IntegrationWebhookLog) => Promise<unknown>;
  resendConfirmDescription?: string;
  getTravellerHref?: (traveller: WebhookLogTraveller) => string | undefined;
  hasListCard?: boolean;
};

export const WebhookLogList = ({
  logs,
  onResend,
  resendConfirmDescription,
  getTravellerHref,
  hasListCard = false,
}: WebhookLogListProps) => {
  const { t } = useScopedTranslate(locale);
  const [selectedLog, setSelectedLog] = useState<IntegrationWebhookLog | undefined>(logs[0]);
  const showConfirmModal = useConfirmModal('LARGE');
  const { numPages, selectedPage, selectPage, getItemsForPage } =
    usePagination<IntegrationWebhookLog>({
      pageSize: LOGS_PER_PAGE,
      defaultPage: 1,
      count: logs.length,
    });

  const logsList = getItemsForPage(logs);

  const handleResendClick = (log: IntegrationWebhookLog) => {
    showConfirmModal({
      title: t('resendWebhook'),
      headerIcon: <IconRedo color="elementPurple100" />,
      buttonText: t('resend'),
      buttonIcon: <IconRedo />,
      buttonVariant: 'primary-blue',
      children: (
        <Stack space={4}>
          <Text font="regular14" text={resendConfirmDescription ?? t('resendConfirmDescription')} />
          <CodeBlock
            header={abbreviateDate(log.dateCreated, 'en', 'datetime-seconds')}
            data={log.requestData}
            maxHeight={pxToRem(320)}
            hideFooter
          />
        </Stack>
      ),
      onConfirm: () => {
        return onResend?.(log);
      },
    });
  };

  const logList = (
    <Stack space={4}>
      <Stack space={2}>
        {logsList.map((log) => (
          <Stack space={2} orientation="horizontal" key={log.shortId} align="center">
            <IconButton icon={<IconSearch />} onClick={() => setSelectedLog(log)} />
            <Stack orientation="horizontal" align="center">
              <Text
                font={selectedLog?.shortId === log.shortId ? 'semibold14' : 'regular14'}
                text={abbreviateDate(log.dateCreated, 'en', 'datetime-seconds')}
              />
              <TravellerIndicator traveller={log.traveller} />
            </Stack>
            {log.resentFrom && <Badge text={t('resent')} />}
          </Stack>
        ))}
      </Stack>
      <Pagination count={numPages} selected={selectedPage} onSelect={selectPage} />
    </Stack>
  );

  return (
    <Stack space={4} orientation="horizontal" justify="space-between">
      <LogColumn>
        {hasListCard ? (
          <Card space={3} variant="gray">
            {logList}
          </Card>
        ) : (
          logList
        )}
      </LogColumn>
      {selectedLog ? (
        <DetailColumn>
          <CodeBlock
            header={
              <HeaderTitle>
                <Text
                  font="bold14"
                  color="textSecondary"
                  text={abbreviateDate(selectedLog.dateCreated, 'en', 'datetime-seconds')}
                />
                <TravellerLabel
                  traveller={selectedLog.traveller}
                  getTravellerHref={getTravellerHref}
                />
              </HeaderTitle>
            }
            headerAction={
              onResend && (
                <IconButton
                  variant="ghost-purple"
                  icon={<IconRedo />}
                  tooltip={t('resend')}
                  dataTestid="resend-webhook-log-btn"
                  onClick={() => handleResendClick(selectedLog)}
                />
              )
            }
            data={selectedLog.requestData}
          />
        </DetailColumn>
      ) : (
        <></>
      )}
    </Stack>
  );
};

const TravellerIndicator = ({ traveller }: { traveller: WebhookLogTraveller | null }) => {
  const { t } = useScopedTranslate(locale);

  if (!traveller) {
    return <></>;
  }

  const tooltip = traveller.hasAccess
    ? t('travellerTooltip', traveller.name ?? '', traveller.journeyTitle ?? '')
    : t('noAccessTooltip');

  return <IconStatusIndicator color="textHelper" tooltip={tooltip} />;
};

const TravellerLabel = ({
  traveller,
  getTravellerHref,
}: {
  traveller: WebhookLogTraveller | null;
  getTravellerHref?: (traveller: WebhookLogTraveller) => string | undefined;
}) => {
  const { t } = useScopedTranslate(locale);

  if (!traveller) {
    return <Text font="regular14" color="textHelper" text={t('noTraveller')} />;
  }

  if (!traveller.hasAccess) {
    return <Text font="regular14" color="textHelper" text={t('noAccess')} />;
  }

  const label = [traveller.name, traveller.journeyTitle].filter(Boolean).join(' · ');
  const href = getTravellerHref?.(traveller);

  if (!href) {
    return <Text font="regular14" text={label} ellipsisWithTooltip />;
  }

  return (
    <ActionText.ExternalLink
      href={href}
      font="regular14"
      color="textPrimary"
      hoverColor="elementPurple100"
      ellipsis
    >
      {label}
    </ActionText.ExternalLink>
  );
};

const HeaderTitle = styled.div({
  minWidth: 0,
});

const LogColumn = styled.div({
  flex: 1,
  minWidth: 0,
});

const DetailColumn = styled.div({
  flex: 2,
  minWidth: 0,
  overflow: 'auto',
});

const locale = {
  en: {
    resend: 'Resend',
    resent: 'Resent',
    noAccess: 'In a journey you cannot access',
    noTraveller: 'No traveller matched',
    travellerTooltip: 'Traveller: {0}\nJourney: {1}',
    noAccessTooltip: 'A traveller in a journey you cannot access',
    resendWebhook: 'Resend webhook',
    resendConfirmDescription:
      'Resend this payload? Waiting webhook actions are checked against it again, and travellers whose conditions match continue their journey.',
  },
};
